import React, { useMemo } from 'react';
import { graphql } from 'gatsby';
import { isEmpty } from 'lodash';
import { GlobalContext } from '../context/GlobalContext';
import Header from '../components/header/Header';
import Footer from '../components/footer/Footer';
import HomerBanner from '../components/home-banner/HomeBanner';
import HomeActivitiesCards from '../components/home-activities-cards/HomeActivitiesCards';
import EventsBlock from '../sections/EventsBlock';
import ExploreBlock from '../components/explore-block/ExploreBlock';
import DownloadMobileApp from '../components/download-mobile-app/DownloadMobileApp';
import PageBanner from '../components/page-banner/PageBanner';
import FullWidthCtaCard from '../components/full-width-cta-card/FullWidthCtaCard';
import SportsActivities from '../components/sports-activities/SportsActivities';
import BookingTypesBlock from '../sections/BookingTypesBlock';
import GalleryBlock from '../sections/GalleryBlock';
import BannerCta from '../components/banner-cta/BannerCta';
import BookingPromotionsBlock from '../sections/BookingPromotionsBlock';
import IntroText from '../components/intro-text/IntroText';
import ActivitiesBlock from '../sections/ActivitiesBlock';
import PlainBanner from '../sections/PlainBanner';
import ContactCards from '../components/contact/ContactCards';
import GMaps from '../components/contact/GoogleMap';
import EventsPaginated from '../components/events-paginated/EventsPaginated';
import Faqs from '../components/faqs/Faqs';
import VenueImage from '../components/venue-list/VenueImage';
import DiningAndShopping from '../components/dining-and-shopping/DiningAndShopping';
import Instagram from '../components/instagram/Instagram';
import Partners from '../components/partners/Partners';
import PromotionsList from '../components/promotions-list/PromotionsList';
import ContactForm from '../components/contact-form/ContactForm';
import MedicalFacilities from '../components/medical-facilities/MedicalFacilities';
import IntroTextWithCard from '../components/intro-text-with-card/IntroTextWithCard';
import HtmlContent from '../sections/HtmlContent';
import AcademiesDetailsIntro from '../components/academies-details-intro/AcademiesDetailsIntro';
import FitnessBooking from '../components/fitness-booking/FitnessBooking';
import SearchResults from '../sections/SearchResults';
import Seo from '../sections/Seo';
import ParkingInfo from '../components/parking-info/ParkingInfo';
import IntroTextWithIconList from '../components/IntroTextWithIconList/IntroTextWithIconList';
import AcademiesCards from '../components/academies-cards/AcademiesCards';
import SportsCards from '../components/sports-cards/SportsCards';
import VideoLoopBlock from '../components/video-block/VideoLoopBlock';

export default function Page({
  data: {
    umbraco: { newDSWPage },
  },

  pageContext: { settings, slug },
  
}) {

  const translations = settings?.translations ? settings?.translations?.map((t) => t.content) : [];

  const values = {
    translations,
    settings,
    slug,
    cssClass: newDSWPage?.cssClass,
  };

  const memo = useMemo(() => values, [values]);

  return (
    <GlobalContext.Provider value={memo}>
      <main>
        <Seo lang="en" page={newDSWPage} dir="ltr" />
        <Header data={settings} />
        {isEmpty(newDSWPage?.newDSWBody) ||
          newDSWPage?.newDSWBody?.map((item, index) => {
            if (!item) return null;
            const id = `${item.content.contentTypeAlias}_${index + 1}`;
            switch (item.content.contentTypeAlias) {
              case 'dswBannerSlides':
                return <HomerBanner key={id} id={id} data={item.content.dswBannerSliderList} />;
              case 'dswActivitiesCards':
                return (
                  <HomeActivitiesCards
                    key={id}
                    id={id}
                    data={item.content.dswActivitiesCardsList}
                  />
                );
              case 'dswUpcomingEvents':
                return <EventsBlock key={id} id={id} data={item.content} />;
              case 'dswPartners':
                return null;
              case 'dswExplore':
                return <ExploreBlock key={id} id={id} data={item.content} />;
              case 'dswAppCTA':
                return <DownloadMobileApp key={id} id={id} data={item.content} />;
              case 'dswPageBanner':
                return <PageBanner key={id} id={id} data={item.content} />;
              case 'dswFullWidthCTA':
                return <FullWidthCtaCard key={id} id={id} data={item.content} />;
              case 'dswSportsActivities':
                return <SportsActivities key={id} id={id} data={item.content} type="sports" />;
              case 'dswFitnessActivities':
                return <ActivitiesBlock key={id} id={id} data={item.content} />;
              case 'dswSportPrices':
                return <BookingTypesBlock key={id} id={id} data={item.content} />;
              case 'dswMediaGallery':
                return <GalleryBlock key={id} id={id} data={item.content} />;
              case 'dswCTA':
                return <BannerCta key={id} id={id} data={item.content} />;
              case 'dswBookingPromotions':
                return <BookingPromotionsBlock key={id} id={id} data={item.content} />;
              case 'dswIntroText':
                return <IntroText key={id} id={id} data={item.content} />;
              case 'dswAcademies':
                return <SportsActivities key={id} data={item.content} />;
              case 'dswAcademiesCards':
                return <AcademiesCards key={id} data={item.content} />;
              case 'dswPageBannerNoImage':
                return <PlainBanner key={id} data={item.content} />;
              case 'dswContactForm':
                return <ContactForm key={id} id={id} data={item.content} />;
              case 'dswAddressCards':
                return <ContactCards key={id} data={item.content} />;
              case 'dswLocationMap':
                return <GMaps key={id} data={item.content} />;
              case 'dswEventsList':
                return <EventsPaginated key={id} id={id} data={item.content} />;
              case 'faqs':
                return <Faqs key={id} id={id} data={item.content.questions} />;
              case 'dswVenueMap':
                return <VenueImage key={id} id={id} data={item.content} />;
              case 'dswDiningAndShopping':
                return <DiningAndShopping key={id} id={id} data={item.content} />;
              case 'dswInstagram':
                return <Instagram key={id} id={id} data={item.content} />;
              case 'dswMedicalFacilities':
                return <MedicalFacilities key={id} id={id} data={item.content} />;
              case 'dswIntroTextWithCard':
                return <IntroTextWithCard key={id} id={id} data={item.content} />;
              case 'dswPartnersList':
                return (
                  <Partners
                    key={id}
                    id={id}
                    data={item.content}
                    partners={
                      item.content.partnersListForSponsors 
                      ? item.content.partnersListForSponsors 
                      : settings.dswPartnersList }
                  />
                );
              case 'dswPromotions':
                return <PromotionsList key={id} id={id} data={item.content} />;
              case 'htmlContent':
                return <HtmlContent key={id} id={id} data={item.content} />;
              case 'dswAcademiesIntroText':
                return <AcademiesDetailsIntro key={id} id={id} data={item.content} />;
              case 'dswBookingCalendar':
                return <FitnessBooking key={id} id={id} data={item.content} />;
              case 'dswParkingInfo':
                return <ParkingInfo key={id} id={id} data={item.content} />;
              case 'searchResults':
                return <SearchResults key={id} id={id} data={item.content} />;
              case 'dswIntroTextWithIconList':
                return <IntroTextWithIconList key={id} id={id} data={item.content} />;
              case 'dswSportsCards':
                return <SportsCards key={id} id={id} data={item.content} />;
              case 'dswVideoLoopBlock':
                return <VideoLoopBlock key={id} id={id} data={item.content} />;
              default:
                return null;
            }
          })}
        <Footer data={settings} />
      </main>
    </GlobalContext.Provider>
  );
}

export const query = graphql`
  query ($id: ID!, $lang: String!) {
    umbraco {
      newDSWPage(id: $id, culture: $lang) {
        ...Page
      }
    }
  }
`;
